import React, { useRef, useEffect } from 'react';
import { Col, Card, Carousel, Image, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { HandThumbsUp, Chat, Share, Flag, Eye } from 'react-bootstrap-icons';
import axios from 'axios';
import { useOnScreen } from '../../hooks/useOnScreen'; // Importe o hook

// O componente agora recebe todas as informações e funções como props
const ServicoCard = ({
    servico,
    isPublishedToday,
    formatRelativeDate,
    onLike,
    onComment,
    onShare,
    onReport,
    onInfoClick,
    isLoggedIn // Recebe o status de login
}) => {
    const cardRef = useRef(null);
    const isVisible = useOnScreen(cardRef);
    const viewRecorded = useRef(false); // Ref para garantir que a view seja registrada apenas uma vez por instância do card

    useEffect(() => {
        // Se o card não está visível ou a visualização já foi registrada para esta instância, não faz nada.
        if (!isVisible || viewRecorded.current) {
            return;
        }

        // Cria uma chave de armazenamento única para usuários logados vs. anônimos.
        // Isso resolve o problema de um novo usuário não ter sua view contada.
        const storageKey = isLoggedIn ? `viewed_servicos_user` : 'viewed_servicos_guest';

        const viewedItems = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        if (viewedItems.includes(servico.id)) {
            viewRecorded.current = true; // Marca como "feito" para esta instância e sai.
            return;
        }

        // Se chegamos aqui, o card está visível e é a primeira vez nesta sessão.
        viewRecorded.current = true; // Marca como registrada para esta instância.

        // Adiciona ao sessionStorage para não registrar novamente nesta sessão.
        viewedItems.push(servico.id);
        sessionStorage.setItem(storageKey, JSON.stringify(viewedItems));

        // Chama a API para registrar a visualização no backend.
        axios.post(`/api/view/servico/${servico.id}`)
            .catch(err => console.error("Falha ao registrar visualização:", err));
            
    }, [isVisible, servico.id, isLoggedIn]);

    return (
        <Col ref={cardRef}>
            <Card className="h-100 shadow service-card border-0 overflow-hidden">
                <div className="service-card-image-container">
                    <Carousel interval={null} indicators={servico.imagens && servico.imagens.length > 1} controls={false}>
                        {(servico.imagens && servico.imagens.length > 0) ? servico.imagens.map(imagem => (
                            <Carousel.Item key={imagem.id}>
                                <Image
                                    src={imagem.url}
                                    alt={`Imagem de ${servico.nome}`}
                                    className="d-block w-100"
                                    style={{ height: '180px', objectFit: 'cover' }}
                                />
                            </Carousel.Item>
                        )) : (
                            <Image src={'https://via.placeholder.com/300x400?text=Sem+Imagem'} className="d-block w-100" style={{ height: '180px', objectFit: 'cover' }} />
                        )}
                    </Carousel>
                    <div className="service-card-image-overlay">
                        {isPublishedToday(servico.updated_at) && (
                            <Badge bg="success" className="me-2">NOVO</Badge>
                        )}
                        {servico.updated_at && (
                            <small>{formatRelativeDate(servico.updated_at)}</small>
                        )}
                    </div>
                </div>
                <Card.Body className="d-flex flex-column p-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <Card.Title as="h6" className="mb-0" title={servico.nome}>{servico.nome}</Card.Title>
                        {servico.disponivel_agendamento_online == 1 && (
                            <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => onInfoClick(servico)}>
                                + Info
                            </Button>
                        )}
                    </div>
                    <div className="flex-grow-1"></div>
                    <div className="mb-2 mt-2">
                        <small className="text-muted">Atendimento em:</small><br />
                        {servico.localidades_publicacao.map(loc => (
                            <Badge key={loc.id} pill bg="light" text="dark" className="me-1 fw-normal">
                                {loc.cidade} - {loc.estado}
                            </Badge>
                        ))}
                    </div>
                    {servico.disponivel_agendamento_online == 1 ? (
                        <>
                            <h5 className="text-primary my-2">R$ {parseFloat(servico.valorOriginal).toFixed(2)}</h5>
                            <Button as={Link} to="/register" variant="primary" className="w-100">Agendar</Button>
                        </>
                    ) : (
                        <p className="text-muted my-2 small" style={{ minHeight: '62px' }}>
                            {servico.descricao_publica || 'Para mais informações sobre este serviço, entre em contato.'}
                        </p>
                    )}
                </Card.Body>
                <Card.Footer className="d-flex justify-content-around align-items-center bg-white border-top-0 pt-0">
                    <span className="d-flex align-items-center text-muted" title="Visualizações">
                        <Eye className="me-1" /> {servico.views_count || 0}
                    </span>
                    <Button variant="light" size="sm" onClick={() => onLike(servico.id)} className="d-flex align-items-center">
                        <HandThumbsUp className="me-1" color={servico.liked_by_user ? 'blue' : 'gray'} /> {servico.likes_count || 0}
                    </Button>
                    <Button variant="light" size="sm" onClick={() => onComment(servico)} className="d-flex align-items-center">
                        <Chat className="me-1" /> {servico.comments_count || 0}
                    </Button>
                    <Button variant="light" size="sm" onClick={() => onShare(servico)} className="d-flex align-items-center">
                        <Share className="me-1" />
                    </Button>
                    <Button variant="light" size="sm" onClick={() => onReport(servico.id)} className="d-flex align-items-center text-danger">
                        <Flag className="me-1" />
                    </Button>
                </Card.Footer>
            </Card>
        </Col>
    );
};

export default ServicoCard;